import { Card, CardContent } from '@/components/ui/card';
import { useMaquinariaGastoDetalle } from '@/hooks/useMaquinariaGastoDetalle';
import { useMaquinariaMovimientos } from '@/hooks/useMaquinariaMovimientos';
import type { PeriodoMaqSel } from '@/lib/cliente';
import { formatearCRC } from '@adelante/shared';
import { CircleNotch, X } from '@phosphor-icons/react';
import { useEffect } from 'react';

export type ModoDrawerMovimientos = 'ingresoPartes' | 'ingresoRecursos' | 'gasto';

export interface DrawerMovimientosSel {
  maquina: string;
  nombreMaquina: string | null;
  modo: ModoDrawerMovimientos;
}

const TITULO_MODO: Record<ModoDrawerMovimientos, string> = {
  ingresoPartes: 'Ingreso — Partes de Trabajo',
  ingresoRecursos: 'Ingreso — Recursos (Ensamblados)',
  gasto: 'Gasto',
};

/**
 * Drawer lateral con el detalle de movimientos que componen una celda de
 * Ingreso Partes / Ingreso Recursos / Gasto en Análisis General de
 * Maquinaria — drill-down pedido para poder auditar los totales.
 */
export function DrawerMovimientosMaquina({
  sel,
  periodoMaq,
  onClose,
}: {
  sel: DrawerMovimientosSel | null;
  periodoMaq?: PeriodoMaqSel;
  onClose: () => void;
}) {
  useEffect(() => {
    if (!sel) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [sel, onClose]);

  const esIngreso = sel?.modo === 'ingresoPartes' || sel?.modo === 'ingresoRecursos';
  const origenUso = sel?.modo === 'ingresoPartes' ? 'PARTE_TRABAJO' : 'ASSEMBLY';

  const movimientos = useMaquinariaMovimientos(
    esIngreso ? (sel?.maquina ?? null) : null,
    esIngreso ? origenUso : null,
    periodoMaq,
  );
  const gasto = useMaquinariaGastoDetalle(
    sel?.modo === 'gasto' ? (sel?.maquina ?? null) : null,
    periodoMaq,
  );

  if (!sel) return null;

  const cargando = esIngreso ? movimientos.isLoading : gasto.isLoading;
  const error = esIngreso ? movimientos.error : gasto.error;
  const filasIngreso = movimientos.data?.data?.filas ?? [];
  const filasGasto = gasto.data?.data ?? [];
  const total = esIngreso
    ? filasIngreso.reduce((s, f) => s + f.montoIngresoMaquinaLcy, 0)
    : filasGasto.reduce((s, f) => s + f.totalCostLcy, 0);
  const cantidad = esIngreso ? filasIngreso.length : filasGasto.length;

  return (
    <>
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: backdrop decorativo, cierre accesible por ESC y botón Cerrar */}
      <div className="fixed inset-0 z-40 bg-black/40" onClick={onClose} aria-hidden="true" />
      <div className="fixed inset-y-0 right-0 z-50 flex w-full max-w-[720px] flex-col bg-zinc-50 shadow-2xl">
        <div className="flex items-center justify-between border-b border-zinc-200 bg-white px-5 py-3">
          <div>
            <div className="text-xs text-muted-foreground">{TITULO_MODO[sel.modo]}</div>
            <div className="text-lg font-semibold">
              {sel.maquina}
              {sel.nombreMaquina && (
                <span className="text-muted-foreground"> — {sel.nombreMaquina}</span>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-2 text-muted-foreground hover:bg-zinc-100"
            aria-label="Cerrar"
            title="Cerrar (ESC)"
          >
            <X className="h-5 w-5" weight="bold" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {cargando && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <CircleNotch className="h-4 w-4 animate-spin" weight="bold" />
              Cargando movimientos…
            </div>
          )}

          {error && (
            <Card className="border-destructive">
              <CardContent className="pt-6 text-sm text-destructive">
                Error: {error.message}
              </CardContent>
            </Card>
          )}

          {!cargando && !error && (
            <>
              <div className="flex items-center justify-between text-xs text-muted-foreground px-1">
                <span>{cantidad.toLocaleString('es-CR')} movimientos</span>
                <span className="font-semibold text-foreground">Total: {formatearCRC(total)}</span>
              </div>

              {esIngreso ? (
                <TablaIngreso filas={filasIngreso} />
              ) : (
                <TablaGasto filas={filasGasto} />
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}

function TablaIngreso({
  filas,
}: {
  filas: {
    entryNo: number;
    fecha: string;
    worksNo: string;
    proyectoDisplay: string | null;
    documentNo: string | null;
    horasUso: number | null;
    tarifaAplicadaLcy: number;
    montoIngresoMaquinaLcy: number;
  }[];
}) {
  if (filas.length === 0) {
    return <p className="text-sm text-muted-foreground py-6 text-center">Sin movimientos.</p>;
  }
  return (
    <div className="overflow-x-auto rounded border border-zinc-200 bg-white">
      <table className="w-full text-xs">
        <thead className="border-b bg-zinc-50">
          <tr className="text-left text-muted-foreground">
            <th className="px-3 py-1.5">Fecha</th>
            <th className="px-3 py-1.5">Obra</th>
            <th className="px-3 py-1.5">Documento</th>
            <th className="px-3 py-1.5 text-right">Horas</th>
            <th className="px-3 py-1.5 text-right">Tarifa</th>
            <th className="px-3 py-1.5 text-right">Monto</th>
          </tr>
        </thead>
        <tbody>
          {filas.map((f) => (
            <tr key={`${f.entryNo}-${f.documentNo}`} className="border-b border-zinc-100">
              <td className="px-3 py-1.5">{f.fecha}</td>
              <td className="px-3 py-1.5">
                {f.worksNo}
                {f.proyectoDisplay && (
                  <span className="text-muted-foreground"> — {f.proyectoDisplay}</span>
                )}
              </td>
              <td className="px-3 py-1.5 font-mono text-[10px]">{f.documentNo ?? '—'}</td>
              <td className="px-3 py-1.5 text-right tabular-nums">
                {f.horasUso != null ? f.horasUso.toLocaleString('es-CR') : '—'}
              </td>
              <td className="px-3 py-1.5 text-right tabular-nums">
                {formatearCRC(f.tarifaAplicadaLcy)}
              </td>
              <td className="px-3 py-1.5 text-right tabular-nums font-medium">
                {formatearCRC(f.montoIngresoMaquinaLcy)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TablaGasto({
  filas,
}: {
  filas: {
    entryNo: number;
    fecha: string;
    documentNo: string | null;
    description: string | null;
    itemCategoryCode: string | null;
    vendorName: string | null;
    totalCostLcy: number;
  }[];
}) {
  if (filas.length === 0) {
    return <p className="text-sm text-muted-foreground py-6 text-center">Sin movimientos.</p>;
  }
  return (
    <div className="overflow-x-auto rounded border border-zinc-200 bg-white">
      <table className="w-full text-xs">
        <thead className="border-b bg-zinc-50">
          <tr className="text-left text-muted-foreground">
            <th className="px-3 py-1.5">Fecha</th>
            <th className="px-3 py-1.5">Descripción</th>
            <th className="px-3 py-1.5">Categoría</th>
            <th className="px-3 py-1.5">Documento</th>
            <th className="px-3 py-1.5 text-right">Costo</th>
          </tr>
        </thead>
        <tbody>
          {filas.map((f) => (
            <tr key={`${f.entryNo}-${f.documentNo}`} className="border-b border-zinc-100">
              <td className="px-3 py-1.5">{f.fecha}</td>
              <td className="px-3 py-1.5">
                <div className="truncate max-w-[260px]">{f.description ?? '—'}</div>
                {f.vendorName && (
                  <div className="text-[10px] text-muted-foreground">{f.vendorName}</div>
                )}
              </td>
              <td className="px-3 py-1.5">{f.itemCategoryCode ?? '(—)'}</td>
              <td className="px-3 py-1.5 font-mono text-[10px]">{f.documentNo ?? '—'}</td>
              <td className="px-3 py-1.5 text-right tabular-nums font-medium">
                {formatearCRC(f.totalCostLcy)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
